import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getAITranslation, GetAITranslationRequest, GetAITranslationResponse, reactivateTranslation, ReactivateTranslationRequest, ReactivateTranslationResponse } from '../../services';

type AITranslationState = {
  inputText: string;
  targetLanguage: string;
  loading: boolean;
  error: string | null;
  result?: GetAITranslationResponse;
  hasTranslated: boolean;
  reactivateLoading: boolean;
  reactivateError: string | null;
  reactivateResult?: ReactivateTranslationResponse;
};

const initialState: AITranslationState = {
  inputText: '',
  targetLanguage: 'es',
  loading: false,
  error: null,
  result: undefined,
  hasTranslated: false,
  reactivateLoading: false,
  reactivateError: null,
  reactivateResult: undefined,
};

export const fetchAITranslation = createAsyncThunk<
  GetAITranslationResponse,
  GetAITranslationRequest
>('aiTranslation/fetchAITranslation', async (payload) => {
  const res = await getAITranslation(payload);
  return res;
});

export const reactivateTranslationAsync = createAsyncThunk<
  ReactivateTranslationResponse,
  ReactivateTranslationRequest,
  { rejectValue: string }
>('aiTranslation/reactivateTranslation', async (payload, { rejectWithValue }) => {
  const response = await reactivateTranslation(payload);
  if (response.status !== 'Success') {
    return rejectWithValue(response.message);
  }
  return response;
});

const aiTranslationSlice = createSlice({
  name: 'aiTranslation',
  initialState,
  reducers: {
    setInputText(state, action: PayloadAction<string>) {
      state.inputText = action.payload;
    },
    setTargetLanguage(state, action: PayloadAction<string>) {
      state.targetLanguage = action.payload;
    },
    clearResult(state) {
      state.result = undefined;
      state.hasTranslated = false;
      state.error = null;
      state.reactivateResult = undefined;
      state.reactivateError = null;
      state.reactivateLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAITranslation.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.hasTranslated = false;
      })
      .addCase(fetchAITranslation.fulfilled, (state, action) => {
        state.loading = false;
        state.result = action.payload;
        state.hasTranslated = true;
      })
      .addCase(fetchAITranslation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Translation failed';
        state.hasTranslated = false;
      })
      .addCase(reactivateTranslationAsync.pending, (state) => {
        state.reactivateLoading = true;
        state.reactivateError = null;
      })
      .addCase(reactivateTranslationAsync.fulfilled, (state, action) => {
        state.reactivateLoading = false;
        state.reactivateResult = action.payload;
      })
      .addCase(reactivateTranslationAsync.rejected, (state, action) => {
        state.reactivateLoading = false;
        state.reactivateError = action.payload || 'Reactivation failed';
      });
  },
});

export const { setInputText, setTargetLanguage, clearResult } = aiTranslationSlice.actions;
export default aiTranslationSlice.reducer;
